import React, { useState, useEffect, useMemo } from "react";
import { confirmDialog } from "../../../utils/confirm";
import { Star, Trash2, Loader2, BookOpen } from "lucide-react";
import { getCourses as apiGetCourses, deleteCourseReview } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const AdminReviews = () => {
  const [courses, setCourses] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterCourseId, setFilterCourseId] = useState("");
  const [isDeleting, setIsDeleting] = useState(null);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const data = await apiGetCourses();
      const mappedCourses = Array.isArray(data) ? data : [];
      setCourses(mappedCourses);

      // Aggregate all reviews across all courses
      const allReviews = mappedCourses.reduce((acc, course) => {
        const courseReviews = (
          Array.isArray(course.reviews) ? course.reviews : []
        ).map((r) => ({
          ...r,
          courseId: course.id,
          courseTitle: course.title,
          // Handle both id formats
          id: r.id || r._id,
        }));
        return [...acc, ...courseReviews];
      }, []);

      // Sort by latest first
      setReviews(
        allReviews.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt) -
            new Date(a.updatedAt || a.createdAt),
        ),
      );
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load reviews",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const filtered = useMemo(() => {
    if (!filterCourseId) return reviews;
    return reviews.filter((r) => String(r.courseId) === String(filterCourseId));
  }, [reviews, filterCourseId]);

  const handleDelete = async (courseId, reviewId) => {
    const ok = await confirmDialog({
      title: "Delete this review?",
      text: "This will permanently remove the review and update the course rating.",
    });
    if (!ok) return;

    setIsDeleting(reviewId);
    try {
      await deleteCourseReview(courseId, reviewId);
      // Refresh local state
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Review has been removed successfully",
        confirmButtonColor: "#38915c",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "Failed to delete review",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (reviews.length === 0) return { avg: 0, total: 0 };
    const total = reviews.length;
    const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
    return {
      avg: (sum / total).toFixed(1),
      total,
    };
  }, [reviews]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full blur-3xl -mr-32 -mt-32"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-50 rounded-xl border border-primary-100">
              <Star className="w-6 h-6 text-primary-600 fill-primary-600/10" />
            </div>
            <span className="text-primary-600 font-bold text-xs uppercase tracking-widest">
              Feedback Center
            </span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">
            Reviews & <span className="text-primary-600">Ratings</span>
          </h1>
          <p className="text-gray-500 text-sm mt-3 max-w-lg leading-relaxed font-medium">
            Monitor and manage student feedback across the platform. Currently
            tracking{" "}
            <span className="text-primary-700 font-bold">{reviews.length}</span>{" "}
            individual submissions.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 relative z-10">
          <div className="flex flex-col gap-1 w-full sm:w-auto">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
              Filter Course
            </label>
            <div className="relative group">
              <select
                id="filter-course"
                value={filterCourseId}
                onChange={(e) => setFilterCourseId(e.target.value)}
                className="appearance-none pl-4 pr-10 py-3 rounded-2xl bg-gray-50 border border-gray-200 text-gray-700 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 outline-none min-w-[240px] text-sm font-bold transition-all group-hover:border-gray-300"
              >
                <option value="">All Platform Courses</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-primary-600 transition-colors">
                <BookOpen size={16} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:border-primary-200 transition-all group">
          <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
            Global Average
          </p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-black text-gray-900">{stats.avg}</h3>
            <div className="flex items-center text-yellow-500 mb-1.5">
              <Star className="w-5 h-5 fill-current" />
            </div>
            <span className="text-gray-400 text-xs mb-1.5 ml-auto font-bold">
              out of 5.0
            </span>
          </div>
          <div className="mt-4 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className="bg-primary-500 h-full rounded-full transition-all duration-1000"
              style={{ width: `${(stats.avg / 5) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:border-primary-200 transition-all">
          <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
            Total Submissions
          </p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-black text-gray-900">{stats.total}</h3>
            <span className="text-gray-400 text-xs mb-1.5 font-bold italic ml-1">
              Verified reviews
            </span>
            <div className="mb-1.5 ml-auto p-1.5 bg-primary-50 rounded-lg">
              <Star className="w-4 h-4 text-primary-600" />
            </div>
          </div>
          <p className="mt-4 text-[10px] text-primary-600 font-bold uppercase tracking-tighter">
            Active Engagement
          </p>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:border-primary-200 transition-all">
          <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
            Response Quality
          </p>
          <div className="flex items-end gap-2">
            <h3 className="text-3xl font-black text-gray-900">High</h3>
            <span className="text-gray-400 text-xs mb-1.5 font-bold ml-1">
              Standards
            </span>
          </div>
          <div className="mt-4 flex gap-1 items-center">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full ${i < 4 ? "bg-primary-500" : "bg-gray-100"}`}
              ></div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold shadow-sm">
          {error}
        </div>
      )}

      {/* Main Table Grid */}
      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-32 text-center px-6">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-gray-100 shadow-inner text-gray-300">
              <BookOpen className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 tracking-tight">
              No Results Found
            </h3>
            <p className="text-gray-500 mt-3 max-w-sm mx-auto font-medium leading-relaxed">
              {filterCourseId
                ? "This course hasn't received any reviews yet. Check back soon!"
                : "The platform's feedback log is currently empty."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="py-5 px-8 font-black text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    Course Module
                  </th>
                  <th className="py-5 px-6 font-black text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    Contributor
                  </th>
                  <th className="py-5 px-6 font-black text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    Assessment
                  </th>
                  <th className="py-5 px-6 font-black text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    Testimonial
                  </th>
                  <th className="py-5 px-6 font-black text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    Timeline
                  </th>
                  <th className="py-5 px-8 font-black text-[10px] text-gray-400 uppercase tracking-[0.2em] text-right">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-primary-50/30 transition-all group"
                  >
                    <td className="py-6 px-8">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 text-sm tracking-tight mb-1">
                          {r.courseTitle}
                        </span>
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                          ID: {String(r.courseId).slice(-6)}
                        </span>
                      </div>
                    </td>
                    <td className="py-6 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                          {r.userName?.charAt(0) || "U"}
                        </div>
                        <span className="font-semibold text-gray-700 text-sm">
                          {r.userName}
                        </span>
                      </div>
                    </td>
                    <td className="py-6 px-6">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((v) => (
                          <Star
                            key={v}
                            className={`w-3.5 h-3.5 ${r.rating >= v ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="py-6 px-6">
                      <div className="max-w-xs xl:max-w-md">
                        <p className="text-gray-600 text-sm leading-relaxed italic line-clamp-2">
                          "{r.review || "No written feedback provided."}"
                        </p>
                      </div>
                    </td>
                    <td className="py-6 px-6">
                      <span className="text-gray-500 text-xs font-bold whitespace-nowrap">
                        {new Date(
                          r.updatedAt || r.createdAt,
                        ).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </td>
                    <td className="py-6 px-8 text-right">
                      <button
                        type="button"
                        disabled={isDeleting === r.id}
                        onClick={() => handleDelete(r.courseId, r.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all disabled:opacity-50"
                        title="Delete review"
                      >
                        {isDeleting === r.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 size={20} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
